import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { getLocationReturnPath, openRecord, useNavigateBack } from '../../../hooks/useNavigateBack';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import TextInput from '../../../components/ui/TextInput';
import { Textarea } from '../../../components/ui/Textarea';
import { Select } from '../../../components/ui/Select';
import { Switch } from '../../../components/ui/Switch';
import Badge from '../../../components/ui/Badge';
import {
  useCrewHoliday,
  type CrewHoliday,
  type CrewHolidayKind,
  type CrewHolidayRecurrence,
} from './useData';
import { moduleConfig, getInitialFormData, mapItemToFormData } from './config';

type FormState = {
  code: string;
  name: string;
  kind: CrewHolidayKind;
  countryCode: string;
  recurrence: CrewHolidayRecurrence;
  month: number;
  dayOfMonth: number;
  weekday: number;
  weekOrdinal: number;
  easterOffset: number;
  sortOrder: number;
  isActive: boolean;
  notes: string;
};

const KIND_OPTIONS = [
  {
    value: 'REGULAR',
    label: 'Regular holiday',
    description: 'Major regular holiday — typically paid at a higher rate.',
  },
  {
    value: 'SPECIAL_NON_WORKING',
    label: 'Special non-working',
    description: 'Special non-working day (e.g. Christmas Eve, All Saints).',
  },
];

const RECURRENCE_OPTIONS = [
  {
    value: 'FIXED_MONTH_DAY',
    label: 'Fixed month & day',
    description: 'Same date every year (e.g. New Year’s Day = January 1).',
  },
  {
    value: 'NTH_WEEKDAY_OF_MONTH',
    label: 'Nth weekday of month',
    description: 'e.g. last Monday of August (National Heroes Day).',
  },
  {
    value: 'EVERY_WEEKDAY',
    label: 'Every weekday',
    description: 'Repeats every week (e.g. every Sunday).',
  },
  {
    value: 'EASTER_OFFSET',
    label: 'Relative to Easter',
    description: 'e.g. Good Friday = Easter − 2 days.',
  },
  {
    value: 'LUNAR_NEW_YEAR',
    label: 'Chinese Lunar New Year',
    description: 'Resolved automatically from the lunar calendar.',
  },
];

const MONTH_OPTIONS = [
  { value: '1', label: 'January' },
  { value: '2', label: 'February' },
  { value: '3', label: 'March' },
  { value: '4', label: 'April' },
  { value: '5', label: 'May' },
  { value: '6', label: 'June' },
  { value: '7', label: 'July' },
  { value: '8', label: 'August' },
  { value: '9', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

const WEEKDAY_OPTIONS = [
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
  { value: '7', label: 'Sunday' },
];

const ORDINAL_OPTIONS = [
  { value: '1', label: 'First' },
  { value: '2', label: 'Second' },
  { value: '3', label: 'Third' },
  { value: '4', label: 'Fourth' },
  { value: '-1', label: 'Last' },
];

const MONTH_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

function normalizeCode(raw: string) {
  return raw.trim().toUpperCase().replace(/\s+/g, '_').slice(0, 40);
}

function FieldShell({
  label,
  required,
  error,
  helperText,
  children,
}: {
  label: string;
  required?: boolean;
  error?: string;
  helperText?: string;
  children: React.ReactNode;
}) {
  const { colors } = useTheme();
  return (
    <div className="space-y-1.5">
      <label className={cn('block text-sm font-medium', colors.text)}>
        {label}
        {required && <span className="ml-0.5 text-red-500">*</span>}
      </label>
      {children}
      {error ? (
        <p className="text-xs text-red-600">{error}</p>
      ) : helperText ? (
        <p className={cn('text-xs', colors.textMuted)}>{helperText}</p>
      ) : null}
    </div>
  );
}

/** Lightweight client preview (mirrors backend FIXED / NTH / EASTER only). */
function previewResolved(form: FormState, year: number): { iso: string; label: string } | null {
  const monthNames = MONTH_OPTIONS.map((m) => m.label);
  if (form.recurrence === 'FIXED_MONTH_DAY') {
    if (form.month < 1 || form.dayOfMonth < 1) return null;
    const dt = new Date(Date.UTC(year, form.month - 1, form.dayOfMonth));
    if (dt.getUTCMonth() !== form.month - 1) return null;
    const iso = dt.toISOString().slice(0, 10);
    return {
      iso,
      label: `Every ${monthNames[form.month - 1]} ${form.dayOfMonth}`,
    };
  }
  if (form.recurrence === 'NTH_WEEKDAY_OF_MONTH') {
    const ordLabel = ORDINAL_OPTIONS.find((o) => Number(o.value) === form.weekOrdinal)?.label ?? '';
    const wdLabel = WEEKDAY_OPTIONS.find((o) => Number(o.value) === form.weekday)?.label ?? '';
    const label = `${ordLabel} ${wdLabel} of ${monthNames[form.month - 1] ?? ''}`;
    // Approximate: ask server for exact; show rule only if we can't compute quickly — compute last Monday etc.
    const isoWeekday = form.weekday;
    let resolved: Date | null = null;
    if (form.weekOrdinal === -1) {
      const last = new Date(Date.UTC(year, form.month, 0)).getUTCDate();
      for (let d = last; d >= last - 6; d--) {
        const dt = new Date(Date.UTC(year, form.month - 1, d));
        const wd = ((dt.getUTCDay() + 6) % 7) + 1;
        if (wd === isoWeekday) {
          resolved = dt;
          break;
        }
      }
    } else {
      let count = 0;
      const dim = new Date(Date.UTC(year, form.month, 0)).getUTCDate();
      for (let d = 1; d <= dim; d++) {
        const dt = new Date(Date.UTC(year, form.month - 1, d));
        const wd = ((dt.getUTCDay() + 6) % 7) + 1;
        if (wd === isoWeekday) {
          count += 1;
          if (count === form.weekOrdinal) {
            resolved = dt;
            break;
          }
        }
      }
    }
    return { iso: resolved ? resolved.toISOString().slice(0, 10) : '', label };
  }
  if (form.recurrence === 'EASTER_OFFSET') {
    // Anonymous Gregorian computus
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    const easter = new Date(Date.UTC(year, month - 1, day));
    const dt = new Date(easter.getTime() + form.easterOffset * 86_400_000);
    const off = form.easterOffset;
    const label =
      off === 0 ? 'Easter Sunday' : off < 0 ? `Easter ${off} day${off === -1 ? '' : 's'}` : `Easter +${off}`;
    return { iso: dt.toISOString().slice(0, 10), label };
  }
  if (form.recurrence === 'LUNAR_NEW_YEAR') {
    return { iso: '', label: 'Chinese Lunar New Year' };
  }
  if (form.recurrence === 'EVERY_WEEKDAY') {
    const wdLabel = WEEKDAY_OPTIONS.find((o) => Number(o.value) === form.weekday)?.label ?? 'weekday';
    return { iso: '', label: `Every ${wdLabel}` };
  }
  return null;
}

const HolidayForm: React.FC = () => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const navigate = useNavigate();
  const location = useLocation();
  const returnPath = getLocationReturnPath(location.state);
  const goBack = useNavigateBack(moduleConfig.module.route.list, returnPath);
  const { id } = useParams<{ id: string }>();
  const editId = id ? Number(id) : NaN;
  const isEdit = Number.isFinite(editId);
  const previewYear = new Date().getFullYear();

  const { data, isLoading, isError } = useCrewHoliday(isEdit ? editId : 0);
  const [form, setForm] = useState<FormState>(() => {
    const init = getInitialFormData();
    return {
      code: String(init.code ?? ''),
      name: String(init.name ?? ''),
      kind: (init.kind as CrewHolidayKind) ?? 'REGULAR',
      countryCode: String(init.countryCode ?? 'PH'),
      recurrence: (init.recurrence as CrewHolidayRecurrence) ?? 'FIXED_MONTH_DAY',
      month: Number(init.month ?? 1),
      dayOfMonth: Number(init.dayOfMonth ?? 1),
      weekday: Number(init.weekday ?? 1),
      weekOrdinal: Number(init.weekOrdinal ?? -1),
      easterOffset: Number(init.easterOffset ?? -2),
      sortOrder: Number(init.sortOrder ?? 0),
      isActive: init.isActive ?? true,
      notes: String(init.notes ?? ''),
    };
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(!isEdit);

  useEffect(() => {
    if (!isEdit || !data) return;
    const mapped = mapItemToFormData(data);
    setForm({
      code: String(mapped.code ?? ''),
      name: String(mapped.name ?? ''),
      kind: (mapped.kind as CrewHolidayKind) ?? 'REGULAR',
      countryCode: String(mapped.countryCode ?? 'PH'),
      recurrence: (mapped.recurrence as CrewHolidayRecurrence) ?? 'FIXED_MONTH_DAY',
      month: Number(mapped.month ?? 1),
      dayOfMonth: Number(mapped.dayOfMonth ?? 1),
      weekday: Number(mapped.weekday ?? 1),
      weekOrdinal: Number(mapped.weekOrdinal ?? -1),
      easterOffset: Number(mapped.easterOffset ?? -2),
      sortOrder: Number(mapped.sortOrder ?? 0),
      isActive: mapped.isActive ?? true,
      notes: String(mapped.notes ?? ''),
    });
    setHydrated(true);
  }, [isEdit, data]);

  const setField = useCallback(<K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const kindMeta = useMemo(() => KIND_OPTIONS.find((o) => o.value === form.kind), [form.kind]);
  const recurrenceMeta = useMemo(
    () => RECURRENCE_OPTIONS.find((o) => o.value === form.recurrence),
    [form.recurrence],
  );
  const preview = useMemo(() => previewResolved(form, previewYear), [form, previewYear]);
  const serverPreview = isEdit && data?.resolvedDate ? data.resolvedDate : preview?.iso;
  const serverLabel = isEdit && data?.ruleLabel ? data.ruleLabel : preview?.label;

  const dateParts = useMemo(() => {
    const iso = serverPreview || preview?.iso;
    if (!iso) return null;
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
    if (!m) return null;
    return { y: Number(m[1]), m: Number(m[2]) - 1, d: Number(m[3]) };
  }, [serverPreview, preview?.iso]);

  const validate = () => {
    const next: Record<string, string> = {};
    if (!form.code.trim()) next.code = 'Code is required';
    if (!form.name.trim()) next.name = 'Name is required';
    if (!form.countryCode.trim()) next.countryCode = 'Country is required';
    if (form.recurrence === 'FIXED_MONTH_DAY') {
      if (form.month < 1 || form.month > 12) next.month = 'Month is required';
      if (form.dayOfMonth < 1 || form.dayOfMonth > 31) next.dayOfMonth = 'Day is required';
    }
    if (form.recurrence === 'NTH_WEEKDAY_OF_MONTH') {
      if (form.month < 1 || form.month > 12) next.month = 'Month is required';
    }
    if (form.recurrence === 'EVERY_WEEKDAY') {
      if (form.weekday < 1 || form.weekday > 7) next.weekday = 'Weekday is required';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    setSaveError(null);
    const payload: Record<string, unknown> = {
      code: normalizeCode(form.code),
      name: form.name.trim(),
      kind: form.kind,
      countryCode: form.countryCode.trim().toUpperCase().slice(0, 2),
      recurrence: form.recurrence,
      isActive: form.isActive,
      sortOrder: Number.isFinite(form.sortOrder) ? form.sortOrder : 0,
      notes: form.notes.trim() || null,
    };
    if (form.recurrence === 'FIXED_MONTH_DAY') {
      payload.month = form.month;
      payload.dayOfMonth = form.dayOfMonth;
    } else if (form.recurrence === 'NTH_WEEKDAY_OF_MONTH') {
      payload.month = form.month;
      payload.weekday = form.weekday;
      payload.weekOrdinal = form.weekOrdinal;
    } else if (form.recurrence === 'EVERY_WEEKDAY') {
      payload.weekday = form.weekday;
    } else if (form.recurrence === 'EASTER_OFFSET') {
      payload.easterOffset = form.easterOffset;
    }
    try {
      const from = returnPath ?? moduleConfig.module.route.list;
      if (isEdit) {
        await moduleConfig.form.update(editId, payload as Partial<CrewHoliday>);
        openRecord(navigate, moduleConfig.module.route.detail(editId), from);
      } else {
        const created = await moduleConfig.form.create(payload as Partial<CrewHoliday>);
        openRecord(navigate, moduleConfig.module.route.detail(created.id), from);
      }
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? String(
              (err as { response?: { data?: { message?: string | string[] } } }).response?.data
                ?.message ?? '',
            )
          : '';
      setSaveError((Array.isArray(msg) ? msg.join(', ') : msg) || moduleConfig.form.errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (isEdit && isLoading && !hydrated) {
    return (
      <div className={cn('flex min-h-[40vh] items-center justify-center text-sm', colors.textMuted)}>
        {moduleConfig.form.loadingText}
      </div>
    );
  }

  if (isEdit && (isError || (!isLoading && !data))) {
    return (
      <div className="mx-auto max-w-lg p-8 text-center">
        <h1 className={cn('text-xl font-semibold', colors.text)}>Holiday not found</h1>
        <p className={cn('mt-2 text-sm', colors.textSecondary)}>
          This holiday does not exist or was deleted.
        </p>
        <Link to={moduleConfig.module.route.list} className="mt-4 inline-block text-sm text-blue-600 underline">
          Back to holidays
        </Link>
      </div>
    );
  }

  const previewCode = normalizeCode(form.code) || 'CODE';
  const previewName = form.name.trim() || 'Holiday name';
  const accent =
    form.kind === 'REGULAR'
      ? isDark
        ? 'from-emerald-900/40 to-transparent'
        : 'from-emerald-50 to-transparent'
      : isDark
        ? 'from-sky-900/40 to-transparent'
        : 'from-sky-50 to-transparent';

  return (
    <div className={cn('min-h-full', colors.background)}>
      <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8">
        <div className="mb-6">
          <Link
            to={isEdit ? moduleConfig.module.route.detail(editId) : moduleConfig.module.route.list}
            className={cn('text-sm font-medium', colors.textSecondary, 'hover:underline')}
          >
            ← {isEdit ? 'Back to details' : 'Back to holidays'}
          </Link>
          <h1 className={cn('mt-2 text-2xl font-semibold tracking-tight', colors.text)}>
            {isEdit ? moduleConfig.form.edit.title : moduleConfig.form.new.title}
          </h1>
          <p className={cn('mt-1 max-w-2xl text-sm', colors.textSecondary)}>
            Define once — fixed dates (like Jan 1) apply every year automatically.
          </p>
        </div>

        {saveError && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {saveError}
          </div>
        )}

        <form onSubmit={onSubmit} className="grid gap-6 lg:grid-cols-12">
          <div className="space-y-5 lg:col-span-7">
            <section className={cn('rounded-xl border p-5 shadow-sm', colors.surface, colors.border)}>
              <div className="mb-4">
                <h2 className={cn('text-base font-semibold', colors.text)}>Identity</h2>
                <p className={cn('mt-0.5 text-sm', colors.textSecondary)}>
                  Stable code plus the name shown on sheets and reports.
                </p>
              </div>
              <div className="grid gap-4 sm:grid-cols-12">
                <div className="sm:col-span-4">
                  <FieldShell label="Code" required error={errors.code} helperText="e.g. NEW_YEAR">
                    <TextInput
                      value={form.code}
                      onChange={(e) => setField('code', e.target.value.toUpperCase())}
                      onBlur={() => setField('code', normalizeCode(form.code))}
                      placeholder="NEW_YEAR"
                      maxLength={40}
                      showClearButton={false}
                    />
                  </FieldShell>
                </div>
                <div className="sm:col-span-8">
                  <FieldShell label="Name" required error={errors.name}>
                    <TextInput
                      value={form.name}
                      onChange={(e) => setField('name', e.target.value)}
                      placeholder="New Year's Day"
                      maxLength={200}
                      showClearButton={false}
                    />
                  </FieldShell>
                </div>
                <div className="sm:col-span-8">
                  <FieldShell label="Kind" required helperText={kindMeta?.description}>
                    <Select
                      value={form.kind}
                      onChange={(e) => setField('kind', e.target.value as CrewHolidayKind)}
                      options={KIND_OPTIONS}
                      showClearButton={false}
                    />
                  </FieldShell>
                </div>
                <div className="sm:col-span-4">
                  <FieldShell label="Country" required error={errors.countryCode} helperText="ISO alpha-2">
                    <TextInput
                      value={form.countryCode}
                      onChange={(e) => setField('countryCode', e.target.value.toUpperCase())}
                      onBlur={() =>
                        setField('countryCode', form.countryCode.trim().toUpperCase().slice(0, 2))
                      }
                      placeholder="PH"
                      maxLength={2}
                      showClearButton={false}
                    />
                  </FieldShell>
                </div>
              </div>
            </section>

            <section className={cn('rounded-xl border p-5 shadow-sm', colors.surface, colors.border)}>
              <div className="mb-4">
                <h2 className={cn('text-base font-semibold', colors.text)}>Recurrence</h2>
                <p className={cn('mt-0.5 text-sm', colors.textSecondary)}>
                  {recurrenceMeta?.description}
                </p>
              </div>
              <div className="grid gap-4 sm:grid-cols-12">
                <div className="sm:col-span-12">
                  <FieldShell label="Rule type" required>
                    <Select
                      value={form.recurrence}
                      onChange={(e) =>
                        setField('recurrence', e.target.value as CrewHolidayRecurrence)
                      }
                      options={RECURRENCE_OPTIONS}
                      showClearButton={false}
                    />
                  </FieldShell>
                </div>

                {form.recurrence === 'FIXED_MONTH_DAY' && (
                  <>
                    <div className="sm:col-span-6">
                      <FieldShell label="Month" required error={errors.month}>
                        <Select
                          value={String(form.month)}
                          onChange={(e) => setField('month', Number(e.target.value))}
                          options={MONTH_OPTIONS}
                          showClearButton={false}
                        />
                      </FieldShell>
                    </div>
                    <div className="sm:col-span-6">
                      <FieldShell
                        label="Day of month"
                        required
                        error={errors.dayOfMonth}
                        helperText="Repeats every year on this day."
                      >
                        <TextInput
                          type="number"
                          value={String(form.dayOfMonth)}
                          onChange={(e) => setField('dayOfMonth', Number(e.target.value) || 0)}
                          min={1}
                          max={31}
                          showClearButton={false}
                        />
                      </FieldShell>
                    </div>
                  </>
                )}

                {form.recurrence === 'NTH_WEEKDAY_OF_MONTH' && (
                  <>
                    <div className="sm:col-span-4">
                      <FieldShell label="Month" required error={errors.month}>
                        <Select
                          value={String(form.month)}
                          onChange={(e) => setField('month', Number(e.target.value))}
                          options={MONTH_OPTIONS}
                          showClearButton={false}
                        />
                      </FieldShell>
                    </div>
                    <div className="sm:col-span-4">
                      <FieldShell label="Weekday" required>
                        <Select
                          value={String(form.weekday)}
                          onChange={(e) => setField('weekday', Number(e.target.value))}
                          options={WEEKDAY_OPTIONS}
                          showClearButton={false}
                        />
                      </FieldShell>
                    </div>
                    <div className="sm:col-span-4">
                      <FieldShell label="Which" required>
                        <Select
                          value={String(form.weekOrdinal)}
                          onChange={(e) => setField('weekOrdinal', Number(e.target.value))}
                          options={ORDINAL_OPTIONS}
                          showClearButton={false}
                        />
                      </FieldShell>
                    </div>
                  </>
                )}

                {form.recurrence === 'EVERY_WEEKDAY' && (
                  <div className="sm:col-span-6">
                    <FieldShell
                      label="Weekday"
                      required
                      error={errors.weekday}
                      helperText="Repeats every week on this day (e.g. Sunday)."
                    >
                      <Select
                        value={String(form.weekday)}
                        onChange={(e) => setField('weekday', Number(e.target.value))}
                        options={WEEKDAY_OPTIONS}
                        showClearButton={false}
                      />
                    </FieldShell>
                  </div>
                )}

                {form.recurrence === 'EASTER_OFFSET' && (
                  <div className="sm:col-span-6">
                    <FieldShell
                      label="Days from Easter Sunday"
                      required
                      helperText="−3 Maundy Thursday, −2 Good Friday, −1 Black Saturday."
                    >
                      <TextInput
                        type="number"
                        value={String(form.easterOffset)}
                        onChange={(e) => setField('easterOffset', Number(e.target.value))}
                        showClearButton={false}
                      />
                    </FieldShell>
                  </div>
                )}

                {form.recurrence === 'LUNAR_NEW_YEAR' && (
                  <div className={cn('sm:col-span-12 rounded-lg border px-4 py-3 text-sm', colors.border, colors.textSecondary)}>
                    Date is computed automatically for each year (no month/day to enter).
                  </div>
                )}
              </div>
            </section>

            <section className={cn('rounded-xl border p-5 shadow-sm', colors.surface, colors.border)}>
              <div className="mb-4">
                <h2 className={cn('text-base font-semibold', colors.text)}>Status & notes</h2>
                <p className={cn('mt-0.5 text-sm', colors.textSecondary)}>
                  Inactive rules are ignored when WRH recomputes the H flag.
                </p>
              </div>
              <div className="grid gap-4 sm:grid-cols-12">
                <div className="sm:col-span-4">
                  <FieldShell label="Sort order">
                    <TextInput
                      type="number"
                      value={String(form.sortOrder)}
                      onChange={(e) => setField('sortOrder', Number(e.target.value) || 0)}
                      showClearButton={false}
                    />
                  </FieldShell>
                </div>
                <div className="flex items-end sm:col-span-8">
                  <div className={cn('w-full rounded-lg border px-4 py-3', colors.border)}>
                    <Switch
                      checked={form.isActive}
                      onChange={(e) => setField('isActive', e.target.checked)}
                      label="Active"
                      description="Only active holidays set the WRH holiday flag."
                    />
                  </div>
                </div>
                <div className="sm:col-span-12">
                  <FieldShell label="Notes">
                    <Textarea
                      value={form.notes}
                      onChange={(e) => setField('notes', e.target.value)}
                      placeholder="Optional note…"
                      rows={3}
                      maxLength={500}
                      showCharacterCount
                    />
                  </FieldShell>
                </div>
              </div>
            </section>

            <div className="flex flex-wrap items-center gap-3 pt-1">
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-blue-700 disabled:opacity-50"
              >
                {saving
                  ? 'Saving…'
                  : isEdit
                    ? moduleConfig.form.edit.submitText
                    : moduleConfig.form.new.submitText}
              </button>
              <button
                type="button"
                onClick={() =>
                  isEdit
                    ? openRecord(
                        navigate,
                        moduleConfig.module.route.detail(editId),
                        returnPath ?? moduleConfig.module.route.list,
                      )
                    : goBack()
                }
                className={cn(
                  'inline-flex items-center rounded-lg border px-4 py-2.5 text-sm font-medium transition',
                  colors.border,
                  colors.text,
                  colors.surface,
                  'hover:opacity-90',
                )}
              >
                {moduleConfig.form.cancelText}
              </button>
            </div>
          </div>

          <aside className="lg:col-span-5">
            <div
              className={cn(
                'sticky top-6 overflow-hidden rounded-xl border shadow-sm',
                colors.surface,
                colors.border,
              )}
            >
              <div className={cn('bg-gradient-to-b px-5 pt-5 pb-4', accent)}>
                <p className={cn('text-xs font-medium uppercase tracking-wide', colors.textMuted)}>
                  Preview · {previewYear}
                </p>
                <div className="mt-3 flex items-start gap-4">
                  <div
                    className={cn(
                      'flex w-[4.5rem] shrink-0 flex-col overflow-hidden rounded-lg border text-center shadow-sm',
                      colors.border,
                      colors.background,
                    )}
                  >
                    <div
                      className={cn(
                        'px-1 py-1 text-[10px] font-semibold uppercase tracking-wider text-white',
                        form.kind === 'REGULAR' ? 'bg-emerald-600' : 'bg-sky-600',
                      )}
                    >
                      {dateParts ? MONTH_SHORT[dateParts.m] : '—'}
                    </div>
                    <div className={cn('py-2 text-2xl font-semibold tabular-nums leading-none', colors.text)}>
                      {dateParts ? dateParts.d : '–'}
                    </div>
                    <div className={cn('pb-2 text-[10px] tabular-nums', colors.textMuted)}>
                      {dateParts ? dateParts.y : previewYear}
                    </div>
                  </div>
                  <div className="min-w-0 flex-1 pt-0.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={cn('font-mono text-xs font-semibold tracking-wide', colors.textMuted)}>
                        {previewCode}
                      </span>
                      <Badge
                        variant={form.kind === 'REGULAR' ? 'success' : 'secondary'}
                        className="rounded-md px-2 py-0.5"
                      >
                        {kindMeta?.label ?? form.kind}
                      </Badge>
                      {!form.isActive && (
                        <Badge variant="danger" className="rounded-md px-2 py-0.5">
                          Inactive
                        </Badge>
                      )}
                    </div>
                    <p className={cn('mt-1.5 text-lg font-semibold leading-snug', colors.text)}>
                      {previewName}
                    </p>
                    <p className={cn('mt-1 text-sm', colors.textSecondary)}>
                      {serverLabel || recurrenceMeta?.label}
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-3 px-5 py-4 text-sm">
                <p className={colors.textSecondary}>
                  {form.recurrence === 'FIXED_MONTH_DAY'
                    ? 'This holiday fires every year on the same month and day — no yearly re-entry.'
                    : form.recurrence === 'EASTER_OFFSET'
                      ? 'Date is calculated from Easter Sunday each year.'
                      : form.recurrence === 'NTH_WEEKDAY_OF_MONTH'
                        ? 'Date is calculated from the weekday rule each year.'
                        : 'Lunar New Year is looked up per year automatically.'}
                </p>
              </div>
            </div>
          </aside>
        </form>
      </div>
    </div>
  );
};

export default HolidayForm;
